import { useEffect } from 'react';
import {
	Button,
	Card,
	Col,
	Form,
	InputNumber,
	Row,
	Space,
	Spin,
	Switch,
	Tag,
	message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError, formatNumber } from '../../utils/helpers';

interface EarningRates {
	route_completion: string;
	route_completion_threshold: string;
	route_pay_enabled: boolean;
	route_pay_geocoded_only: boolean;
	daily_allowance: string;
	daily_allowance_min_hours: string;
	daily_allowance_min_visits: number;
	daily_allowance_checkout_at_office: boolean;
	reactivation_bonus: string;
	reactivation_dormant_days: number;
	reactivation_visit_window_days: number;
	recruitment_approval: string;
	recruitment_first_sale: string;
	recruitment_first_sale_days: number;
	sale_commission: string;
	updated_at: string;
}

// Roughly what a full month looks like at a given set of rates, so a change can
// be judged against what an agent takes home rather than in the abstract.
const WORKING_DAYS = 24;
const TARGET_REACTIVATIONS = 3;
const TARGET_RECRUITS = 1;
const TARGET_SALES = 4;

export default function FieldEarningRates() {
	const queryClient = useQueryClient();
	const [form] = Form.useForm();

	const { data: rates, isLoading, error } = useQuery<EarningRates>({
		queryKey: ['field-earning-rates'],
		queryFn: async () => {
			const res = await axios.get('/admin/field/earning-rates', { withCredentials: true });
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	useEffect(() => {
		if (rates) form.setFieldsValue(rates);
	}, [rates, form]);

	const save = useMutation({
		mutationFn: async (values: Partial<EarningRates>) => {
			const res = await axios.put('/admin/field/earning-rates', values, {
				withCredentials: true,
			});
			return res.data.results;
		},
		onSuccess: () => {
			message.success('Rates updated. They apply to work settled from now on.');
			queryClient.invalidateQueries({ queryKey: ['field-earning-rates'] });
		},
		onError: handleAPIError,
	});

	// Live projection from whatever is currently in the form.
	const watched = Form.useWatch([], form) as EarningRates | undefined;
	const projection = (() => {
		const source = watched ?? rates;
		if (!source) return null;

		const route = source.route_pay_enabled
			? Number(source.route_completion || 0) * WORKING_DAYS
			: 0;
		const allowance = Number(source.daily_allowance || 0) * WORKING_DAYS;
		const reactivation = Number(source.reactivation_bonus || 0) * TARGET_REACTIVATIONS;
		const recruit =
			(Number(source.recruitment_approval || 0) + Number(source.recruitment_first_sale || 0)) *
			TARGET_RECRUITS;
		const sales = Number(source.sale_commission || 0) * TARGET_SALES;

		return {
			route,
			allowance,
			reactivation,
			recruit,
			sales,
			total: route + allowance + reactivation + recruit + sales,
		};
	})();

	if (isLoading) {
		return (
			<AppLayout title="Agent Earning Rates" subtitle="What each field sales activity pays">
				<div style={{ padding: 48, textAlign: 'center' }}>
					<Spin />
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout title="Agent Earning Rates" subtitle="What each field sales activity pays">
			<div style={{ padding: 16 }}>
				<Row gutter={16}>
					<Col xs={24} lg={15}>
						<Form
							form={form}
							layout="vertical"
							initialValues={rates}
							onFinish={(values) => save.mutate(values)}
						>
							<Card
								size="small"
								title="Daily activity"
								style={{ marginBottom: 16 }}
								extra={
									<Form.Item
										name="route_pay_enabled"
										valuePropName="checked"
										noStyle
									>
										<Switch checkedChildren="On" unCheckedChildren="Off" />
									</Form.Item>
								}
							>
								<Row gutter={12}>
									<Col xs={24} sm={8}>
										<Form.Item
											name="route_completion"
											label="Route completion (per day)"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={500}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="route_completion_threshold"
											label="Stops required"
											tooltip="0.80 = 80% of stops"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												max={1}
												step={0.05}
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="route_pay_geocoded_only"
											label="Mapped retailers only"
											valuePropName="checked"
											tooltip="Unmapped retailers excluded"
										>
											<Switch />
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card size="small" title="Daily allowance" style={{ marginBottom: 16 }}>
								<Row gutter={12}>
									<Col xs={24} sm={8}>
										<Form.Item
											name="daily_allowance"
											label="Full day allowance"
											tooltip="0 switches it off"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={1000}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="daily_allowance_min_hours"
											label="Hours for a full day"
											tooltip="Check-in to check-out, exclusive"
										>
											<InputNumber style={{ width: '100%' }} min={0} max={24} step={0.5} />
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="daily_allowance_min_visits"
											label="Unflagged visits required"
											tooltip="0 pays on hours alone"
										>
											<InputNumber style={{ width: '100%' }} min={0} />
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="daily_allowance_checkout_at_office"
											label="Check out at the office"
											valuePropName="checked"
											tooltip="Costs the allowance to anyone finishing in the field"
										>
											<Switch />
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card size="small" title="Growing the base" style={{ marginBottom: 16 }}>
								<Row gutter={12}>
									<Col xs={24} sm={8}>
										<Form.Item
											name="reactivation_bonus"
											label="Reactivation bonus"
											tooltip="Visited retailer sells again"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={1000}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="reactivation_dormant_days"
											label="Dormant after (days)"
										>
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="reactivation_visit_window_days"
											label="Visit within (days)"
											tooltip="Visit precedes sale by ≤ this"
										>
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="recruitment_approval"
											label="Retailer recruited"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={1000}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="recruitment_first_sale"
											label="Their first sale"
											tooltip="Paid on their first sale"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={1000}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={8}>
										<Form.Item
											name="recruitment_first_sale_days"
											label="First sale within (days)"
										>
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card size="small" title="Sales" style={{ marginBottom: 16 }}>
								<Row gutter={12}>
									<Col xs={24} sm={8}>
										<Form.Item
											name="sale_commission"
											label="Per qualifying device"
											tooltip="Excludes recovered and defaulted"
										>
											<InputNumber
												style={{ width: '100%' }}
												min={0}
												step={1000}
												addonBefore="UGX"
											/>
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Space>
								<Button type="primary" htmlType="submit" loading={save.isPending}>
									Save rates
								</Button>
								<Button onClick={() => rates && form.setFieldsValue(rates)}>
									Reset
								</Button>
								{rates?.updated_at && (
									<span style={{ color: '#888', fontSize: 12 }}>
										Last changed {dayjs(rates.updated_at).format('DD MMM YYYY, HH:mm')}
									</span>
								)}
							</Space>
						</Form>
					</Col>

					<Col xs={24} lg={9}>
						<Card size="small" title={`Projected month · ${WORKING_DAYS}d · ${TARGET_REACTIVATIONS} react · ${TARGET_RECRUITS} recruit · ${TARGET_SALES} sales`} style={{ position: 'sticky', top: 16 }}>
							{projection && (
								<Space direction="vertical" size={8} style={{ width: '100%' }}>
									<Row justify="space-between">
										<span>Route completion</span>
										<span>
											{projection.route === 0 ? (
												<Tag color="default">off</Tag>
											) : (
												`UGX ${formatNumber(projection.route)}`
											)}
										</span>
									</Row>
									<Row justify="space-between">
										<span>Reactivations</span>
										<span>UGX {formatNumber(projection.reactivation)}</span>
									</Row>
									<Row justify="space-between">
										<span>Recruitment</span>
										<span>UGX {formatNumber(projection.recruit)}</span>
									</Row>
									<Row justify="space-between">
										<span>Sales</span>
										<span>UGX {formatNumber(projection.sales)}</span>
									</Row>
									<Row
										justify="space-between"
										style={{ borderTop: '1px solid #eee', paddingTop: 8, fontWeight: 600 }}
									>
										<span>Per agent / month</span>
										<span>UGX {formatNumber(projection.total)}</span>
									</Row>
									<Row justify="space-between" style={{ color: '#888', fontSize: 12 }}>
										<span>Four agents</span>
										<span>UGX {formatNumber(projection.total * 4)}</span>
									</Row>
								</Space>
							)}
						</Card>
					</Col>
				</Row>
			</div>
		</AppLayout>
	);
}
